import React from 'react';
import {useTranslation} from 'react-i18next';
import BorderRadiusChange from '../../../../components/slides/highlighted/common/borderRadiusChange';
import {IBorderRadius} from '../../../../incase-domain/common/borderRadius';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {getPresentationThemeFontSizeOptions} from '../../../../utils/presentationTheme';
import ColorPicker from '../../../components/common/ColorPicker';
import Label from '../../../../incase-domain/components/label/Label';
import Select from '../../../components/common/Select';

interface PresentationThemeTextElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
}

function PresentationThemeTextElement(props: PresentationThemeTextElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme} = props;
    const {t} = useTranslation();

    const options = getPresentationThemeFontSizeOptions(presentationTheme.fontSizes);
    const textTheme = presentationTheme.elementStyles?.textElement;

    const updatePresentationTheme = <T extends keyof NonNullable<Required<IPresentationTheme>['elementStyles']['textElement']>>(
        key: T,
        value: NonNullable<Required<IPresentationTheme>['elementStyles']['textElement']>[T]
    ): void => {
        setPresentationTheme((prevTheme) => ({
            ...prevTheme,
            elementStyles: {
                ...prevTheme.elementStyles,
                textElement: {
                    ...prevTheme.elementStyles?.textElement,
                    [key]: value,
                },
            },
        }));
    };

    return (
        <div className="u-mt-2">
            <div className="u-pb-2">
                <strong>{t('presentationThemesPage.text.textElement')}</strong>
            </div>
            <div className="l-grid l-grid--noGaps u-flex u-h-between u-v-center u-pb-2">
                <Label size="lg">{t('presentationThemesPage.text.textColor')}</Label>
                <ColorPicker
                    color={textTheme?.color}
                    onChange={(val) => updatePresentationTheme('color', val as string)}
                    secondary
                    forPresentation={false}
                />
            </div>
            <div className="l-grid l-grid--noGaps u-flex u-h-between u-v-center u-pb-2">
                <Label size="lg">{t('presentationThemesPage.text.textBackgroundColor')}</Label>
                <ColorPicker
                    color={textTheme?.backgroundColor}
                    onChange={(val) => updatePresentationTheme('backgroundColor', val as string)}
                    secondary
                    forPresentation={false}
                />
            </div>
            <div className="l-grid l-grid--noGaps u-flex u-h-between u-v-center u-pb-2">
                <Label size="lg">{t('presentationThemesPage.text.textBorderRadius')}</Label>
                <BorderRadiusChange
                    borderRadius={textTheme?.borderRadius}
                    highlightedElementChangeHandler={(val) => updatePresentationTheme('borderRadius', val as IBorderRadius)}
                />
            </div>
            <div className="u-flex u-h-between u-v-center l-grid u-pb-2 l-grid--noGaps ">
                <div className="l-col:4">
                    <Label size="lg">{t('presentationThemesPage.text.textElementSize')}</Label>
                </div>
                <div className="l-col:4">
                    <Select
                        value={options.find((option) => option.label.split(':')[0] === textTheme?.fontSize?.size)}
                        onChange={(newValue) =>
                            newValue &&
                            updatePresentationTheme('fontSize', {
                                size: newValue.label.split(':')[0] as FONT_SIZE,
                                value: parseFloat(newValue.value as string),
                            })
                        }
                        options={options}
                        placeholder={t('presentationThemesPage.text.select')}
                    />
                </div>
            </div>
        </div>
    );
}

export default PresentationThemeTextElement;
